/**
 * ============================================================================
 * Iron Meridian - Action Panel Component
 * ============================================================================
 * 
 * Provides action buttons and forms for player actions like Attack, Build, Research.
 * Premium design with polished interactions and validations.
 */

import { useState, useMemo } from 'react'
import { useUISound } from '../audio/hooks/useAudio.js'

const ACTION_TYPES = {
  attack: {
    icon: '⚔️',
    label: 'Attack',
    color: 'from-red-600 to-red-700',
    activeColor: 'from-red-500 to-red-600',
    borderColor: 'border-red-500',
    description: 'Launch a military assault on a neighboring territory'
  },
  build: {
    icon: '🏗️',
    label: 'Build',
    color: 'from-amber-600 to-amber-700',
    activeColor: 'from-amber-500 to-amber-600',
    borderColor: 'border-amber-500',
    description: 'Construct infrastructure to strengthen your empire'
  },
  fortify: {
    icon: '🏰',
    label: 'Fortify',
    color: 'from-green-600 to-green-700',
    activeColor: 'from-green-500 to-green-600',
    borderColor: 'border-green-500',
    description: 'Strengthen homeland defenses against enemy attacks'
  },
  research: {
    icon: '🔬',
    label: 'Research',
    color: 'from-blue-600 to-blue-700',
    activeColor: 'from-blue-500 to-blue-600',
    borderColor: 'border-blue-500',
    description: 'Open Tech Tree to research technologies',
    opensTechTree: true
  },
  diplomacy: {
    icon: '🌐',
    label: 'Diplomacy',
    color: 'from-purple-600 to-purple-700',
    activeColor: 'from-purple-500 to-purple-600',
    borderColor: 'border-purple-500',
    description: 'Manage relationships with other nations',
    opensDiplomacy: true
  }
}

const BUILDINGS = [
  { 
    id: 'factory', 
    name: 'Factory', 
    cost: 50, 
    buildTime: 2,
    effect: '+15 Income/turn', 
    description: 'Increases your income generation, which indirectly grows your economy stat over time',
    icon: '🏭', 
    color: 'text-yellow-400' 
  },
  { 
    id: 'barracks', 
    name: 'Barracks', 
    cost: 40, 
    buildTime: 1,
    effect: '+8 Military/turn', 
    description: 'Boosts military regeneration every turn, allowing faster army growth',
    icon: '🎖️', 
    color: 'text-red-400' 
  },
  { 
    id: 'university', 
    name: 'University', 
    cost: 60, 
    buildTime: 3,
    effect: '+2 Tech/turn', 
    description: 'Provides passive technology gain each turn, accelerating research',
    icon: '🎓', 
    color: 'text-blue-400' 
  },
  { 
    id: 'fortress', 
    name: 'Fortress', 
    cost: 80, 
    buildTime: 3,
    effect: '+15% Defense', 
    description: 'Strengthens defensive combat power when your territory is attacked',
    icon: '🏰', 
    color: 'text-green-400' 
  },
]

export default function ActionPanel({ selectedCountry, playerCountry, countries, gameState, onAddAction, onOpenTechTree, techTreeOpen, onOpenDiplomacy, diplomacyOpen, pendingProposals = [] }) {
  const [activeAction, setActiveAction] = useState(null)
  const [selectedTarget, setSelectedTarget] = useState(null)
  const [selectedBuilding, setSelectedBuilding] = useState(null)
  const { click, confirm } = useUISound()

  // Get valid attack targets (neighbors of ALL player-owned countries)
  const validTargets = useMemo(() => {
    if (!countries) return []
    
    const targets = new Set()
    
    Object.values(countries).forEach(country => {
      if (country.controller === 'player' && country.neighbors) {
        country.neighbors.forEach(neighborId => {
          const neighbor = countries[neighborId]
          if (neighbor && neighbor.controller !== 'player') {
            targets.add(neighborId)
          }
        })
      }
    })
    
    return Array.from(targets)
  }, [countries])

  // Check if selected country is a valid target
  const isValidTarget = selectedCountry && validTargets.includes(selectedCountry.id)

  // Handle action submission
  const handleSubmitAction = () => {
    if (!activeAction) return

    confirm() // Play confirm sound
    let action = { type: activeAction }

    switch (activeAction) {
      case 'attack':
        if (!selectedTarget && !isValidTarget) return
        action.target = selectedTarget || selectedCountry.id
        action.source = gameState.playerCountry
        break
      case 'build':
        if (!selectedBuilding) return
        action.building = selectedBuilding
        action.target = gameState.playerCountry
        break
      case 'research':
        action.target = gameState.playerCountry
        break
      case 'fortify':
        action.target = gameState.playerCountry
        break
    }

    onAddAction(action)
    setActiveAction(null)
    setSelectedTarget(null)
    setSelectedBuilding(null)
  }

  const canSubmit = activeAction && (
    (activeAction === 'attack' && (selectedTarget || isValidTarget)) ||
    (activeAction === 'build' && selectedBuilding) ||
    activeAction === 'fortify'
    // Research is handled through TechTreePanel, not queued as action
  )

  return (
    <div className="p-5 border-b border-war-room-600/30">
      <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
        <span className="text-war-room-accent">⚡</span>
        Strategic Actions
      </h2>

      {/* Action Type Buttons */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {Object.entries(ACTION_TYPES).map(([type, config]) => (
          <button
            key={type}
            onClick={() => {
              click()
              // Research only opens tech tree modal (no action panel)
              if (type === 'research' && onOpenTechTree) {
                setActiveAction(null)
                onOpenTechTree()
              } else if (type === 'diplomacy' && onOpenDiplomacy) {
                setActiveAction(null)
                onOpenDiplomacy()
              } else {
                setActiveAction(activeAction === type ? null : type)
              }
            }}
            className={`
              action-btn p-3 rounded-xl text-left transition-all duration-200 relative
              ${(activeAction === type) || (type === 'research' && techTreeOpen) || (type === 'diplomacy' && diplomacyOpen)
                ? `bg-gradient-to-br ${config.activeColor} ring-2 ring-white/20 shadow-lg` 
                : 'bg-war-room-700/60 hover:bg-war-room-600/80 border border-war-room-600/30'
              }
            `}
          >
            <div className="flex items-center gap-2.5">
              <span className="text-xl">{config.icon}</span>
              <span className="font-medium text-sm">{config.label}</span>
            </div>
            {/* Pending proposals badge for diplomacy */}
            {type === 'diplomacy' && pendingProposals.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 rounded-full text-xs flex items-center justify-center text-white animate-pulse">
                {pendingProposals.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Action Details Panel */}
      {activeAction && (
        <div className={`bg-war-room-800/60 rounded-xl p-4 border-l-4 ${ACTION_TYPES[activeAction].borderColor} animate-fade-in`}>
          <div className="text-sm text-gray-400 mb-4">
            {ACTION_TYPES[activeAction].description}
          </div>

          {/* Attack Action */}
          {activeAction === 'attack' && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Target Territory</label>
                {isValidTarget ? (
                  <div className="flex items-center gap-3 p-3 bg-war-room-700/60 rounded-xl border border-green-500/30">
                    <div
                      className="w-8 h-8 rounded-lg shadow-sm ring-1 ring-white/10"
                      style={{ backgroundColor: selectedCountry.color }}
                    />
                    <div className="flex-1">
                      <span className="font-medium">{selectedCountry.name}</span>
                      <div className="text-xs text-gray-500">Selected on map</div>
                    </div>
                    <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">✓ Valid</span>
                  </div>
                ) : (
                  <select
                    value={selectedTarget || ''}
                    onChange={(e) => setSelectedTarget(e.target.value)}
                    className="w-full bg-war-room-700/60 rounded-xl p-3 text-sm border border-war-room-600/50 focus:border-war-room-accent focus:outline-none transition-colors"
                  >
                    <option value="">Select a target...</option>
                    {validTargets.map(target => (
                      <option key={target} value={target}>
                        {countries[target]?.name || target}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {validTargets.length === 0 && (
                <div className="flex items-center gap-2 text-xs text-yellow-400 bg-yellow-500/10 p-2 rounded-lg">
                  <span>⚠️</span>
                  <span>No valid targets — expand your borders first</span>
                </div>
              )}
            </div>
          )}

          {/* Build Action */}
          {activeAction === 'build' && (
            <div className="space-y-2">
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Select Building</label>
              {BUILDINGS.map(building => (
                <button
                  key={building.id}
                  onClick={() => setSelectedBuilding(building.id)}
                  title={building.description}
                  className={`
                    w-full p-3 rounded-xl text-left transition-all group relative
                    ${selectedBuilding === building.id 
                      ? 'bg-amber-600/20 border-2 border-amber-500 shadow-lg' 
                      : 'bg-war-room-700/50 hover:bg-war-room-600/50 border border-war-room-600/30'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{building.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm mb-0.5">{building.name}</div>
                      <div className={`text-xs ${building.color} mb-1`}>{building.effect}</div>
                      <div className="text-xs text-gray-500">{building.description}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        ⏱️ {building.buildTime} turn{building.buildTime > 1 ? 's' : ''} to complete
                      </div>
                    </div>
                    <div className="text-xs text-yellow-400 font-semibold">💰 {building.cost}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Fortify Action */}
          {activeAction === 'fortify' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-green-500/20 flex items-center justify-center">
                <span className="text-3xl">🏰</span>
              </div>
              <p className="text-sm text-gray-300 mb-2">
                Strengthen homeland defenses
              </p>
              <p className="text-sm text-green-400 font-medium bg-green-500/10 rounded-lg py-2 px-3 inline-block">
                +10% Defense Bonus
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmitAction}
            disabled={!canSubmit}
            className={`
              w-full mt-4 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2
              ${!canSubmit
                ? 'bg-war-room-700 text-gray-500 cursor-not-allowed'
                : `bg-gradient-to-r ${ACTION_TYPES[activeAction].activeColor} text-white shadow-lg hover:shadow-xl active:scale-[0.98]`
              }
            `}
          >
            <span>Queue {ACTION_TYPES[activeAction].label}</span>
            {canSubmit && <span className="text-lg">→</span>}
          </button>
        </div>
      )}

      {/* Hint when no action selected */}
      {!activeAction && (
        <div className="text-center py-6 text-gray-500">
          <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-war-room-700/50 flex items-center justify-center">
            <span className="text-xl opacity-50">⚡</span>
          </div>
          <p className="text-sm">Select an action type</p>
          <p className="text-xs mt-1">to queue strategic commands</p>
        </div>
      )}
    </div>
  )
}
